import React, { useContext, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Button, CircularProgress } from '@mui/material'

import { AuthContext } from '../../../context/AuthContext'
import { rememberReturnPath } from '../../../tool/returnPath'
import Navbar from '../../workspace/components/Navbar'

const box = { maxWidth: '480px', textAlign: 'center', padding: '0 20px' }

// The workspace's own loading frame (WorkspacePage's "Loading Session..."), on /session/:id too.
// Its Navbar keeps a student on the exercise (no My Exercises, no wordmark link).
const Frame = ({ children }) => (
  <div className="home">
    <Navbar sessionId={null} lockedToExercise />
    <div
      className="container"
      style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
      {children}
    </div>
  </div>
)

// The blocked state's way out. Its text was the end of the road: /login sends a student who is
// still signed in to the session list, which does not show this exercise. This signs them out
// and opens /login with this link as `from` (and kept, tool/returnPath.js), so their sign-in -
// a real account instead of a guest, or a fresh one - comes straight back here.
const SignInAgainButton = () => {
  const { handleSignOut } = useContext(AuthContext)
  const location = useLocation()
  const navigate = useNavigate()
  const [signingOut, setSigningOut] = useState(false)

  const signInAgain = async () => {
    setSigningOut(true)
    rememberReturnPath(location.pathname)
    try {
      await handleSignOut()
    } catch (error) {
      console.error('Failed to sign out:', error)
    }
    // Signed out, the page's sign-in guard may already have sent them to /login with the same
    // `from`: replacing that entry keeps one.
    navigate('/login', { replace: true, state: { from: location } })
  }

  return (
    <Button variant="outlined" onClick={signInAgain} disabled={signingOut}>
      Sign in again
    </Button>
  )
}

// What a student sees while useExperimentEntry works out their page (never a blank page),
// or when there is nothing to open. Students are not told about conditions or experiments.
const ExperimentEntryScreen = ({ entry }) => {
  if (entry.status === 'redirect') return <Navigate to={entry.to} replace />

  if (entry.status === 'blocked') {
    return (
      <Frame>
        <div style={box}>
          <h2 style={{ marginBottom: '8px' }}>{entry.title}</h2>
          <p style={{ color: '#6b7280', marginBottom: entry.signIn || entry.retry ? '16px' : undefined }}>{entry.message}</p>
          {/* Only where signing in can open the link (useExperimentEntry's signIn). */}
          {entry.signIn && <SignInAgainButton />}
          {/* A session that was not found (missingSession): the page also checks again by itself. */}
          {entry.retry && (
            <Button variant="outlined" onClick={entry.retry}>
              Try again
            </Button>
          )}
        </div>
      </Frame>
    )
  }

  if (entry.status === 'slow') {
    return (
      <Frame>
        <div style={box}>
        <h2 style={{ marginBottom: '8px' }}>Still connecting…</h2>
        <p style={{ color: '#6b7280', marginBottom: '16px' }}>
          The server is busy right now. This page keeps trying on its own - you don&apos;t need to refresh.
        </p>
        <Button variant="outlined" onClick={entry.retry}>
          Try again now
        </Button>
        </div>
      </Frame>
    )
  }

  return (
    <Frame>
      <div style={box}>
        <CircularProgress size={28} />
        <p style={{ color: '#6b7280', marginTop: '16px' }}>Opening your exercise…</p>
      </div>
    </Frame>
  )
}

export default ExperimentEntryScreen
